import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { useNavigate } from 'react-router-dom';

import DashboardLayout from '../layout/DashboardLayout';
import Spinner from '../common/Spinner';
import ConfirmDialog from '../common/ConfirmDialog';
import EventDialog from './EventDialog';
import CalendarSidebar from './CalendarSidebar';
import CalendarManagerDialog from './CalendarManagerDialog';
import NotificationSettingsDialog from './NotificationSettingsDialog';
import { config } from '../../config/config';

import {
  fetchEvents, createEvent, updateEvent,
  fetchProjectQueue, sendTomorrowDigest
} from '../../store/slices/eventsSlice';
import { fetchCalendars } from '../../store/slices/calendarsSlice';
import { fetchCrews } from '../../store/slices/crewsSlice';
import { fetchUsers } from '../../store/slices/usersSlice';
import { showToast } from '../utils/Toast';
import { useThemeColors } from '../../hooks/useThemeColors';

import '../../styles/calendar.css';

function useIsMobile(breakpoint = 900) {
  const [mobile, setMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return mobile;
}

const locales = { 'en-US': require('date-fns/locale/en-US') };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const DnDCalendar = withDragAndDrop(Calendar);
const SUPPORTED_VIEWS = ['month', 'week', 'day'];

const normalizeUnavailableWindow = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;

  s.setHours(0, 0, 0, 0);
  const endIsMidnight = (
    e.getHours() === 0 &&
    e.getMinutes() === 0 &&
    e.getSeconds() === 0 &&
    e.getMilliseconds() === 0
  );
  if (endIsMidnight) {
    e.setHours(23, 59, 59, 999);
  }
  if (e.getTime() < s.getTime()) {
    return { start: s, end: new Date(s.getTime() + DAY_MS - 1) };
  }

  return { start: s, end: e };
};

const isCrewUnavailable = (crew, eventStart, eventEnd) => {
  if (!crew?.unavailability) return false;
  const s1 = new Date(eventStart).getTime();
  const e1 = new Date(eventEnd || eventStart).getTime();

  return crew.unavailability.some((u) => {
    const window = normalizeUnavailableWindow(u.start, u.end);
    if (!window) return false;
    const s2 = window.start.getTime();
    const e2 = window.end.getTime();
    return s1 <= e2 && e1 >= s2;
  });
};

const DAY_MS = 24 * 60 * 60 * 1000;

const isMidnight = (d) => (
  d.getHours() === 0 &&
  d.getMinutes() === 0 &&
  d.getSeconds() === 0 &&
  d.getMilliseconds() === 0
);

const EVENT_PALETTE = [
  '#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#0EA5E9', '#F97316', '#84CC16',
  '#06B6D4', '#A855F7', '#E11D48', '#059669', '#D97706',
];

const autoColor = (id = '') => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return EVENT_PALETTE[h % EVENT_PALETTE.length];
};

// Single source of truth for how event colors are resolved in UI.
// Prefer explicitly assigned event color; then crew color; otherwise use stable hash color.
const resolveEventColor = (eventLike = {}, fallbackId = '', crewsList = []) => {
  const explicit = eventLike?.color;
  if (explicit && typeof explicit === 'string') return explicit;
  // Check crew color
  const crewId = eventLike?.crew?._id || eventLike?.crew;
  if (crewId && crewsList.length > 0) {
    const crew = crewsList.find((c) => String(c._id) === String(crewId));
    if (crew?.color) return crew.color;
  }
  const stableId = String(eventLike?._id || fallbackId || '');
  return autoColor(stableId);
};

const hexToRgba = (hex, alpha = 1) => {
  const fallback = `rgba(99, 102, 241, ${alpha})`;
  if (typeof hex !== 'string') return fallback;
  let value = hex.trim().replace('#', '');
  if (value.length === 3) {
    value = value.split('').map((ch) => ch + ch).join('');
  }
  if (value.length !== 6) return fallback;
  const int = Number.parseInt(value, 16);
  if (Number.isNaN(int)) return fallback;
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const normalizeEventRange = ({
  start,
  end,
  allDay,
  inferAllDayFromMidnight = false,
  timedFallbackMinutes = 60
}) => {
  const s = start instanceof Date ? new Date(start) : new Date(start);
  let e = end ? (end instanceof Date ? new Date(end) : new Date(end)) : null;

  const looksDateRange = !!e && isMidnight(s) && isMidnight(e) && e.getTime() > s.getTime();
  const normalizedAllDay = Boolean(allDay) || (inferAllDayFromMidnight && looksDateRange);

  if (normalizedAllDay) {
    s.setHours(0, 0, 0, 0);
    if (!e || e.getTime() <= s.getTime()) {
      e = new Date(s.getTime() + DAY_MS);
    }
    // Convert exclusive next-day midnight into inclusive end-of-day.
    if (isMidnight(e) && e.getTime() > s.getTime()) {
      e = new Date(e.getTime() - 1);
    }
    if (e.getTime() <= s.getTime()) {
      e = new Date(s.getTime() + DAY_MS - 1);
    }
    return { start: s, end: e, allDay: true };
  }

  if (!e || e.getTime() <= s.getTime()) {
    e = new Date(s.getTime() + timedFallbackMinutes * 60 * 1000);
  }

  return { start: s, end: e, allDay: false };
};

const normalizeOutsideDropRange = ({ start, end, allDay, view }) => {
  const s = start instanceof Date ? new Date(start) : new Date(start);
  const e = end ? (end instanceof Date ? new Date(end) : new Date(end)) : null;
  const looksDateRange = !!e && isMidnight(s) && isMidnight(e) && e.getTime() > s.getTime();

  // Month cells represent date ranges; always create all-day events there.
  if (view === 'month') {
    return normalizeEventRange({
      start: s,
      end: e,
      allDay: true,
      inferAllDayFromMidnight: true
    });
  }

  // Week/day all-day gutter drops should remain all-day.
  if (allDay) {
    return normalizeEventRange({
      start: s,
      end: e,
      allDay: true,
      inferAllDayFromMidnight: true
    });
  }

  // Timed area in week/day should not become a 24h midnight block.
  if (looksDateRange) {
    return normalizeEventRange({
      start: s,
      end: new Date(s.getTime() + 60 * 60 * 1000),
      allDay: false
    });
  }

  return normalizeEventRange({ start: s, end: e, allDay: false });
};

const normalizeCrewBusyRange = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;

  const endIsMidnight = (
    e.getHours() === 0 &&
    e.getMinutes() === 0 &&
    e.getSeconds() === 0 &&
    e.getMilliseconds() === 0
  );
  if (endIsMidnight) {
    e.setHours(23, 59, 59, 999);
  }
  if (e.getTime() < s.getTime()) return null;

  return { start: s, end: e };
};

const getCrewBusyIntervals = (crew, events = []) => {
  const manual = (crew?.unavailability || [])
    .map((u) => normalizeCrewBusyRange(u.start, u.end))
    .filter(Boolean)
    .map((range) => ({ start: range.start, end: range.end }));

  const scheduled = (events || [])
    .filter((ev) => {
      const evCrewId = ev?.crew?._id || ev?.crew;
      if (!evCrewId || !crew?._id) return false;
      if (String(evCrewId) !== String(crew._id)) return false;
      return Boolean(ev.start && ev.end);
    })
    .map((ev) => normalizeCrewBusyRange(ev.start, ev.end))
    .filter(Boolean)
    .map((range) => ({ start: range.start, end: range.end }));

  return [...manual, ...scheduled].sort((a, b) => a.start - b.start);
};

const getCrewNextAvailableStart = (crew, events = [], from = new Date()) => {
  const baseline = from instanceof Date ? new Date(from) : new Date(from);
  const baselineTs = baseline.getTime();
  if (Number.isNaN(baselineTs)) return new Date();

  let candidateTs = baselineTs;
  const intervals = getCrewBusyIntervals(crew, events);
  for (const interval of intervals) {
    const s = interval.start.getTime();
    const e = interval.end.getTime();
    if (Number.isNaN(s) || Number.isNaN(e)) continue;
    if (e <= candidateTs) continue;
    if (s > candidateTs) break;
    candidateTs = e;
  }

  return new Date(candidateTs);
};

const CalendarPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { buttonColors } = useThemeColors();
  const currentUser = useSelector((s) => s.auth.user);
  const allUsers = useSelector((s) => s.users.users || []);
  const usersLoading = useSelector((s) => s.users.loading);
  const role = currentUser?.profile?.role;
  const { events, loading } = useSelector((s) => s.events);
  const { items: crews } = useSelector((s) => s.crews);
  const calendars = useSelector((s) => s.calendars.items);
  const visibleIds = useSelector((s) => s.calendars.visibleIds);

  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [prefillSlot, setPrefillSlot] = useState(null);
  const [showManager, setShowManager] = useState(false);
  const [showNotifSettings, setShowNotifSettings] = useState(false);
  const [showSidebar, setShowSidebar] = useState(() => window.innerWidth > 900);
  const isMobile = useIsMobile(900);
  const [outsideDragItem, setOutsideDragItem] = useState(null);
  const [calendarKey, setCalendarKey] = useState(0);
  const [showDigestConfirm, setShowDigestConfirm] = useState(false);
  const [digestUserSearch, setDigestUserSearch] = useState('');
  const [digestSending, setDigestSending] = useState(false);
  const [activeCrewId, setActiveCrewId] = useState(null);
  const [hideTrades, setHideTrades] = useState(false);
  const suppressNextSlotSelectRef = useRef(false);
  const safeView = SUPPORTED_VIEWS.includes(view) ? view : 'week';

  useEffect(() => {
    dispatch(fetchCalendars());
    dispatch(fetchCrews());
    dispatch(fetchEvents());
    dispatch(fetchProjectQueue());
    // Fetch calendar preferences (hideTrades)
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${config.apiUrl}/api/users/calendar-preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success) setHideTrades(!!data.calendarHideTrades);
        }
      } catch (_) { /* silent */ }
    })();
  }, [dispatch]);

  useEffect(() => {
    if (!SUPPORTED_VIEWS.includes(view)) {
      setView('month');
    }
  }, [view]);

  const visibleCalendarIds = useMemo(() => {
    if (visibleIds === null) return null; // null => all
    return new Set(visibleIds);
  }, [visibleIds]);

  const calendarIdsByUserId = useMemo(() => {
    const map = new Map();
    (calendars || []).forEach((cal) => {
      const rawUser = cal?.user;
      const userId = rawUser && typeof rawUser === 'object'
        ? (rawUser._id || rawUser.id || null)
        : rawUser;
      if (!userId || !cal?._id) return;
      const uid = String(userId);
      if (!map.has(uid)) map.set(uid, new Set());
      map.get(uid).add(String(cal._id));
    });
    return map;
  }, [calendars]);

  const calendarIdsByCrewId = useMemo(() => {
    const map = new Map();
    (calendars || []).forEach((cal) => {
      if (cal?.type !== 'crew') return;
      const rawCrew = cal?.crew;
      const crewId = rawCrew && typeof rawCrew === 'object'
        ? (rawCrew._id || rawCrew.id || null)
        : rawCrew;
      if (!crewId || !cal?._id) return;
      const cid = String(crewId);
      if (!map.has(cid)) map.set(cid, new Set());
      map.get(cid).add(String(cal._id));
    });
    return map;
  }, [calendars]);

  useEffect(() => {
    if (!activeCrewId) return;
    const exists = (crews || []).some((crew) => String(crew._id) === String(activeCrewId));
    if (!exists) setActiveCrewId(null);
  }, [activeCrewId, crews]);

  const visibleCrewIds = useMemo(() => {
    if (activeCrewId) return new Set([String(activeCrewId)]);

    const allCrewIds = new Set((crews || []).map((c) => String(c._id)));
    if (visibleIds === null) return allCrewIds;

    const crewIds = new Set();
    (calendars || []).forEach((cal) => {
      if (!visibleIds.includes(cal._id)) return;
      if (cal.type !== 'crew') return;
      const crewId = cal.crew?._id || cal.crew;
      if (crewId) crewIds.add(String(crewId));
    });
    return crewIds;
  }, [activeCrewId, calendars, crews, visibleIds]);

  // Filter events by selected calendars
  const calendarEvents = useMemo(() => {
    return (events || [])
      .filter((e) => {
        if (activeCrewId) {
          const evCrewId = e?.crew?._id || e?.crew;
          return Boolean(evCrewId && String(evCrewId) === String(activeCrewId));
        }
        if (!visibleCalendarIds) return true;

        // Match if ANY linked scope is visible (own calendar, crew calendar,
        // or invited member calendars). This prevents newly-created recurring
        // series from disappearing when one scope is toggled off.
        const linkedCalendarIds = new Set();

        const ownCalendarId = e.calendar?._id || e.calendar;
        if (ownCalendarId) linkedCalendarIds.add(String(ownCalendarId));

        // Multi-calendar assignment: include every linked calendar.
        (e.calendars || []).forEach((c) => {
          const id = c?._id || c;
          if (id) linkedCalendarIds.add(String(id));
        });

        const evCrewId = e?.crew?._id || e?.crew;
        if (evCrewId) {
          const crewCalIds = calendarIdsByCrewId.get(String(evCrewId));
          if (crewCalIds) crewCalIds.forEach((id) => linkedCalendarIds.add(String(id)));
        }

        (e.members || []).forEach((m) => {
          const id = m?._id || m;
          if (!id) return;
          const ids = calendarIdsByUserId.get(String(id));
          if (ids) ids.forEach((cid) => linkedCalendarIds.add(String(cid)));
        });

        if (linkedCalendarIds.size === 0) return true;
        for (const id of linkedCalendarIds) {
          if (visibleCalendarIds.has(id)) return true;
        }
        return false;
      })
      .flatMap((e) => {
        if (e.allDay) {
          // Normalize allDay events to local midnight boundaries so react-big-calendar
          // computes the correct day-span regardless of the stored UTC time component.
          const s = new Date(e.start);
          s.setHours(0, 0, 0, 0);
          const en = new Date(e.end);
          en.setHours(23, 59, 59, 999);
          if (en.getTime() < s.getTime() + DAY_MS - 1) en.setTime(s.getTime() + DAY_MS - 1);
          const resolvedColor = resolveEventColor(e, e._id, crews);
          const banner = {
            id: e._id,
            title: e.title,
            start: s,
            end: en,
            allDay: true,
            resource: { ...e, color: resolvedColor }
          };
          if (view === 'month') return [banner];

          // Week/day need a time-grid fill so all-day crew assignments visually
          // occupy the whole day. Keep this in the foreground event layer rather
          // than backgroundEvents; RBC stacks backgroundEvents independently and
          // can visually swap colors when multiple all-day ranges overlap.
          const fill = {
            id: `${e._id}-fill`,
            title: e.title,
            start: s,
            end: en,
            allDay: false,
            resource: {
              ...e,
              kind: 'allday_fill',
              color: resolvedColor
            }
          };
          return [banner, fill];
        }
        return [{
          id: e._id,
          title: e.title,
          start: new Date(e.start),
          end: new Date(e.end),
          allDay: false,
          resource: e
        }];
      });
  }, [activeCrewId, calendarIdsByCrewId, calendarIdsByUserId, events, view, visibleCalendarIds, crews]);

  const unavailableBackgroundEvents = useMemo(() => {
    const windows = [];

    (crews || []).forEach((crew) => {
      const crewId = String(crew._id);
      if (!visibleCrewIds.has(crewId)) return;

      (crew.unavailability || []).forEach((u, idx) => {
        if (!u?.start || !u?.end) return;
        const window = normalizeUnavailableWindow(u.start, u.end);
        if (!window) return;
        windows.push({
          id: `crew-manual-${crewId}-${idx}`,
          title: `${crew.name} unavailable`,
          start: window.start,
          end: window.end,
          allDay: true,
          resource: {
            kind: 'crew_unavailability',
            source: 'manual',
            crewId,
            crewName: crew.name,
            crewColor: crew.color,
            reason: u.reason || ''
          }
        });
      });
    });

    // NOTE:
    // We intentionally do NOT paint scheduled events into the background layer.
    // Scheduled items already render as foreground event cards; duplicating them
    // as background blocks causes full-day colored columns in week/day view that
    // look like extra events with mismatched colors.

    return windows;
  }, [crews, visibleCrewIds]);

  const eventStyleGetter = useCallback((evt) => {
    const e = evt.resource || {};
    const color = resolveEventColor(e, evt.id, crews);
    const crewObj = crews.find((c) => c._id === (e.crew?._id || e.crew));
    const faded = crewObj && isCrewUnavailable(crewObj, evt.start, evt.end);
    return {
      className: `cal-event-card ${e.kind === 'allday_fill' ? 'is-allday-fill' : ''} ${faded ? 'is-faded' : ''}`,
      style: {
        ['--accent']: color,
        ['--event-bg']: color,
        ['--event-border']: color,
        backgroundColor: color,
        color: '#fff',
        border: `1px solid ${color}`,
        borderRadius: 10,
        padding: 0,
        boxShadow: '0 1px 4px rgba(15, 23, 42, 0.18)',
        opacity: faded ? 0.5 : 1,
        filter: faded ? 'grayscale(0.4)' : 'none',
        cursor: 'pointer',
        overflow: 'hidden'
      }
    };
  }, [crews]);

  const backgroundEventStyleGetter = useCallback((evt) => {
    const bg = evt.resource || {};

    if (bg.kind === 'allday_fill') {
      const base = bg.eventColor || '#6366f1';
      return {
        className: 'cal-allday-fill-bg',
        style: {
          backgroundColor: hexToRgba(base, 0.26),
          border: `1px solid ${hexToRgba(base, 0.42)}`,
          borderRadius: 6
        }
      };
    }

    const base = bg.crewColor || '#94a3b8';
    const isScheduled = bg.source === 'scheduled';
    return {
      className: `cal-unavail-bg ${isScheduled ? 'is-scheduled' : 'is-manual'}`,
      style: {
        backgroundColor: hexToRgba(base, isScheduled ? 0.08 : 0.14),
        border: `1px dashed ${hexToRgba(base, isScheduled ? 0.26 : 0.38)}`,
        borderRadius: 6
      }
    };
  }, []);

  const handleSelectSlot = ({ start, end }) => {
    if (suppressNextSlotSelectRef.current) {
      suppressNextSlotSelectRef.current = false;
      return;
    }

    if (role === 'crew_member' || role === 'crew_leader') return; // crew members/leaders view-only
    const crewCalendar = activeCrewId
      ? (calendars || []).find((c) => {
        if (c.type !== 'crew') return false;
        const calendarCrewId = c.crew?._id || c.crew;
        return calendarCrewId && String(calendarCrewId) === String(activeCrewId);
      })
      : null;

    setSelectedEvent(null);
    setPrefillSlot({
      start,
      end,
      ...(activeCrewId ? {
        crewId: activeCrewId,
        eventType: 'crew_assignment',
        calendarId: crewCalendar?._id || ''
      } : {})
    });
    setDialogOpen(true);
  };

  const handleSelectEvent = (evt) => {
    suppressNextSlotSelectRef.current = true;
    setTimeout(() => {
      suppressNextSlotSelectRef.current = false;
    }, 0);

    const direct = evt?.resource || evt?.event || evt;
    const directId = direct?._id || direct?.id || evt?._id || evt?.id;

    const findByIdentity = () => {
      if (direct?._id) return direct;
      if (directId) {
        return (events || []).find((x) => String(x._id) === String(directId)) || null;
      }

      const startTs = direct?.start ? new Date(direct.start).getTime() : NaN;
      const endTs = direct?.end ? new Date(direct.end).getTime() : NaN;
      const title = String(direct?.title || '').trim();
      if (Number.isNaN(startTs) || Number.isNaN(endTs)) return null;

      return (events || []).find((x) => {
        if (!x?.start || !x?.end) return false;
        const xs = new Date(x.start).getTime();
        const xe = new Date(x.end).getTime();
        if (Number.isNaN(xs) || Number.isNaN(xe)) return false;
        if (xs !== startTs || xe !== endTs) return false;
        if (!title) return true;
        return String(x.title || '').trim() === title;
      }) || null;
    };

    const e = findByIdentity();
    // Ignore transient outside-drag preview objects that have no persisted id.
    if (!e || !e._id) return;
    // If event is linked to a project and user clicks the icon area, navigate
    setSelectedEvent(e);
    setPrefillSlot(null);
    setDialogOpen(true);
  };

  const handleEventDrop = async ({ event, start, end, isAllDay }) => {
    if (!event?.resource?._id) return; // outside-drag ghost or unresolved event
    if (event.resource.kind === 'allday_fill') return; // fill is a visual companion only
    const e = event.resource;
    const normalized = normalizeEventRange({ start, end, allDay: isAllDay ?? e.allDay });
    const stableTitle = String(event.title || e.title || '').trim();
    await dispatch(updateEvent({
      id: e._id,
      updates: {
        ...(stableTitle ? { title: stableTitle } : {}),
        start: normalized.start,
        end: normalized.end,
        allDay: normalized.allDay,
      }
    }));
  };

  const handleEventResize = async ({ event, start, end, isAllDay }) => {
    if (!event?.resource?._id) return; // outside-drag ghost or unresolved event
    if (event.resource.kind === 'allday_fill') return; // fill is a visual companion only
    const e = event.resource;
    const normalized = normalizeEventRange({ start, end, allDay: isAllDay ?? e.allDay });
    const stableTitle = String(event.title || e.title || '').trim();
    await dispatch(updateEvent({
      id: e._id,
      updates: {
        ...(stableTitle ? { title: stableTitle } : {}),
        start: normalized.start,
        end: normalized.end,
        allDay: normalized.allDay,
      }
    }));
  };

  // Drop a queue item onto a slot via react-big-calendar's outside-drop API
  const onDropFromOutside = useCallback(async ({ start, end, allDay }) => {
    // Crew drag (from sidebar Crews tab) — open EventDialog with crew prefilled so
    // the user can optionally link a project, edit the title, etc., before saving.
    const isCrew = outsideDragItem?.__isCrew || !!window.__draggedCrewItem;
    if (isCrew) {
      const crew = outsideDragItem || window.__draggedCrewItem;
      window.__draggedCrewItem = null;
      setOutsideDragItem(null);
      const normalized = normalizeOutsideDropRange({ start, end, allDay, view });

      // Conflict guard: don't even open the dialog if the crew can't take this slot.
      const startTs = normalized.start.getTime();
      const endTs = normalized.end.getTime();
      const unavailableHit = (crew?.unavailability || []).some((u) => {
        const s = new Date(u.start);
        const e = new Date(u.end);
        if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return false;
        if (e.getHours() === 0 && e.getMinutes() === 0 && e.getSeconds() === 0 && e.getMilliseconds() === 0) {
          e.setHours(23, 59, 59, 999);
        }
        return startTs < e.getTime() && endTs > s.getTime();
      });
      const scheduledHit = (events || []).some((ev) => {
        const evCrewId = ev?.crew?._id || ev?.crew;
        if (!evCrewId || String(evCrewId) !== String(crew._id)) return false;
        const s = new Date(ev.start).getTime();
        const e = new Date(ev.end).getTime();
        if (Number.isNaN(s) || Number.isNaN(e)) return false;
        return startTs < e && endTs > s;
      });
      if (unavailableHit || scheduledHit) {
        showToast(`${crew.name} is unavailable or already scheduled for this time`, 'error');
        return;
      }

      setSelectedEvent(null);
      setPrefillSlot({
        start: normalized.start,
        end: normalized.end,
        allDay: normalized.allDay,
        crewId: String(crew._id),
        title: crew.name
      });
      setDialogOpen(true);
      return;
    }

    // Check if it's a person drag
    const isPerson = outsideDragItem?.__isPerson || !!window.__draggedPersonItem;
    if (isPerson) {
      const person = outsideDragItem || window.__draggedPersonItem;
      window.__draggedPersonItem = null;
      setOutsideDragItem(null);
      const normalized = normalizeOutsideDropRange({ start, end, allDay, view });

      // Block drop if person already has an event overlapping this slot
      const personConflict = (events || []).find((ev) => {
        const evMembers = (ev.members || []).map((m) => String(m._id || m));
        if (!evMembers.includes(String(person._id))) return false;
        if (!ev.start || !ev.end) return false;
        const s = new Date(ev.start).getTime();
        const e = new Date(ev.end).getTime();
        if (Number.isNaN(s) || Number.isNaN(e)) return false;
        return normalized.start.getTime() < e && normalized.end.getTime() > s;
      });
      if (personConflict) {
        showToast(
          `${person.name} already has "${personConflict.title || 'an event'}" at this time`,
          'error'
        );
        setCalendarKey((k) => k + 1);
        return;
      }

      const personCalendar = (calendars || []).find((c) => {
        const rawUser = c?.user;
        const userId = rawUser && typeof rawUser === 'object'
          ? (rawUser._id || rawUser.id || null)
          : rawUser;
        return userId && String(userId) === String(person._id);
      });
      await dispatch(createEvent({
        title: person.name,
        description: '',
        start: normalized.start,
        end: normalized.end,
        allDay: normalized.allDay,
        calendar: personCalendar?._id || null,
        eventType: 'event',
        members: [String(person._id)],
        visibility: 'all',
      })).unwrap();
      await dispatch(fetchEvents());
      return;
    }

    // Project drag — open EventDialog with the project auto-linked so the user
    // can select a crew, edit title, etc., before saving.
    const project = outsideDragItem || window.__draggedQueueItem;
    if (!project) return;
    window.__draggedQueueItem = null;
    setOutsideDragItem(null);
    const normalized = normalizeOutsideDropRange({
      start,
      end,
      allDay,
      view
    });

    // Build a suggested title from the project
    const clientName = project.client?.name || '';
    const suggestedTitle = [project.jobNumber, clientName].filter(Boolean).join(' — ') || project.title || '';

    // Build a location from the project address
    const addr = project.address || {};
    const suggestedLocation = [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ');

    setSelectedEvent(null);
    setPrefillSlot({
      start: normalized.start,
      end: normalized.end,
      allDay: normalized.allDay,
      title: suggestedTitle,
      location: suggestedLocation,
      projectId: project._id,
      projectMeta: project
    });
    setDialogOpen(true);
  }, [calendars, dispatch, outsideDragItem, view, crews, events]);

  const dragFromOutsideItem = useCallback(() => {
    const p = outsideDragItem || window.__draggedQueueItem || window.__draggedPersonItem || window.__draggedCrewItem;
    if (!p) return null;
    const start = new Date();
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const title = p.__isCrew
      ? p.name
      : p.__isPerson
        ? `Event with ${p.name}`
        : (p.title || p.name || 'New Event');
    return { title, start, end, allDay: view === 'month' };
  }, [outsideDragItem, view]);

  const handleDragOverFromOutside = useCallback((e) => {
    if (outsideDragItem || window.__draggedQueueItem || window.__draggedPersonItem || window.__draggedCrewItem) e.preventDefault();
  }, [outsideDragItem]);

  const handleSendDigest = async () => {
    dispatch(fetchUsers());
    setDigestUserSearch('');
    setShowDigestConfirm(true);
  };

  const confirmSendDigest = async (email) => {
    setDigestSending(true);
    try {
      const result = await dispatch(sendTomorrowDigest(email)).unwrap();
      showToast(`Digest sent to ${email} (${result.count || 0} events)`, 'success');
      setShowDigestConfirm(false);
    } catch (err) {
      showToast(err || 'Failed to send digest', 'error');
    } finally {
      setDigestSending(false);
    }
  };

  const handleCrewSelect = useCallback((crewId) => {
    setActiveCrewId(crewId ? String(crewId) : null);
  }, []);

  const handleScheduleWithCrew = useCallback((crew) => {
    if (!crew?._id) return;
    const crewId = String(crew._id);
    const nextStart = getCrewNextAvailableStart(crew, events, new Date());
    const nextEnd = new Date(nextStart.getTime() + 60 * 60 * 1000);

    const crewCalendar = (calendars || []).find((c) => {
      if (c.type !== 'crew') return false;
      const calendarCrewId = c.crew?._id || c.crew;
      return calendarCrewId && String(calendarCrewId) === crewId;
    });

    setActiveCrewId(crewId);
    setSelectedEvent(null);
    setPrefillSlot({
      start: nextStart,
      end: nextEnd,
      crewId,
      eventType: 'crew_assignment',
      title: crew.name,
      calendarId: crewCalendar?._id || ''
    });
    setDialogOpen(true);
  }, [calendars, events]);

  const calendarThemeVars = useMemo(() => ({
    '--cal-accent': buttonColors.primary,
    '--cal-accent-soft': hexToRgba(buttonColors.primary, 0.12),
    '--cal-primary': buttonColors.primary,
    '--cal-primary-hover': buttonColors.primaryHover,
    '--cal-primary-active': buttonColors.primaryActive
  }), [buttonColors]);

  return (
    <DashboardLayout hideGreyMenu={true}>
      <div className="calendar-shell" style={calendarThemeVars}>
        {showSidebar && isMobile && (
          <div className="cal-sidebar-backdrop" onClick={() => setShowSidebar(false)} />
        )}
        {showSidebar && (
          <CalendarSidebar
            onClose={() => setShowSidebar(false)}
            onCreateEvent={(prefill) => {
              setSelectedEvent(null);
              setPrefillSlot(prefill || null);
              setDialogOpen(true);
              if (isMobile) setShowSidebar(false);
            }}
            onManageCalendars={() => {
              setShowManager(true);
              if (isMobile) setShowSidebar(false);
            }}
            onQueueDragStart={(project) => setOutsideDragItem(project)}
            onQueueDragEnd={() => setOutsideDragItem(null)}
            onCrewDragStart={(crew) => { window.__draggedCrewItem = crew; setOutsideDragItem({ ...crew, __isCrew: true }); }}
            onCrewDragEnd={() => { window.__draggedCrewItem = null; setOutsideDragItem(null); }}
            activeCrewId={activeCrewId}
            onCrewSelect={handleCrewSelect}
            onScheduleWithCrew={(crew) => { handleScheduleWithCrew(crew); if (isMobile) setShowSidebar(false); }}
            hideTrades={hideTrades}
            onToggleHideTrades={async () => {
              const next = !hideTrades;
              setHideTrades(next);
              try {
                const token = localStorage.getItem('token');
                await fetch(`${config.apiUrl}/api/users/calendar-preferences`, {
                  method: 'PUT',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ calendarHideTrades: next })
                });
              } catch (_) { /* silent */ }
            }}
          />
        )}

        <div className="calendar-main">
          <div className="calendar-toolbar-top">
            <div className="flex items-center gap-2">
              {(!showSidebar || isMobile) && (
                <button className="cal-btn" onClick={() => setShowSidebar(true)} title="Open sidebar">
                  ☰
                </button>
              )}
              <h1 className="calendar-title">Calendar</h1>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button className="cal-btn" onClick={() => setShowNotifSettings(true)}>
                Notifications
              </button>
              {(role === 'super_admin' || role === 'admin') && (
                <button className="cal-btn" onClick={handleSendDigest}>
Send Tomorrow’s Schedule
                </button>
              )}
              <button
                className="cal-btn cal-btn-primary"
                onClick={() => { setSelectedEvent(null); setPrefillSlot(null); setDialogOpen(true); }}
              >
                + New Event
              </button>
            </div>
          </div>

          <div id="calendar-grid-root" className="calendar-grid-root">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <Spinner size="xl" color="blue" />
              </div>
            ) : (
              <DnDCalendar
                key={calendarKey}
                localizer={localizer}
                events={calendarEvents}
                backgroundEvents={unavailableBackgroundEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                view={safeView}
                date={date}
                onView={(nextView) => setView(SUPPORTED_VIEWS.includes(nextView) ? nextView : 'month')}
                onNavigate={setDate}
                views={SUPPORTED_VIEWS}
                showMultiDayTimes
                showAllEvents={safeView === 'month'}
                selectable="ignoreEvents"
                resizable
                popup
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                onDoubleClickEvent={handleSelectEvent}
                onEventDrop={handleEventDrop}
                onEventResize={handleEventResize}
                onDropFromOutside={onDropFromOutside}
                dragFromOutsideItem={dragFromOutsideItem}
                onDragOver={handleDragOverFromOutside}
                eventPropGetter={eventStyleGetter}
                backgroundEventPropGetter={backgroundEventStyleGetter}
                className="custom-calendar premium-calendar"
                components={{
                  event: ({ event }) => (
                    <CalendarEventTile
                      evt={event}
                      navigate={navigate}
                      crews={crews}
                      onOpenEvent={handleSelectEvent}
                      hideTrades={hideTrades}
                    />
                  )
                }}
              />
            )}
          </div>
        </div>
      </div>

      <EventDialog
        isOpen={dialogOpen}
        onClose={() => { setDialogOpen(false); setSelectedEvent(null); setPrefillSlot(null); }}
        event={selectedEvent}
        prefill={prefillSlot}
      />

      {showManager && (
        <CalendarManagerDialog
          isOpen={showManager}
          onClose={() => setShowManager(false)}
        />
      )}

      {showNotifSettings && (
        <NotificationSettingsDialog
          isOpen={showNotifSettings}
          onClose={() => setShowNotifSettings(false)}
        />
      )}

      {/* ── Send tomorrow's digest – user picker ── */}
      {showDigestConfirm && (() => {
        const search = digestUserSearch.toLowerCase();
        const filtered = allUsers.filter((u) => {
          const name = displayName(u).toLowerCase();
          const email = (u.email || '').toLowerCase();
          return !search || name.includes(search) || email.includes(search);
        });
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.45)' }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowDigestConfirm(false); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl flex flex-col"
              style={{ width: 420, maxHeight: '80vh', overflow: 'hidden' }}
            >
              {/* Header */}
              <div
                className="flex items-center justify-between px-5 py-4 border-b border-gray-100"
                style={{ borderRadius: '16px 16px 0 0' }}
              >
                <div>
                  <div className="text-gray-800 font-semibold text-base">Send Tomorrow's Schedule</div>
                  <div className="text-gray-400 text-xs mt-0.5">Select a user to send the digest to</div>
                </div>
                <button
                  onClick={() => setShowDigestConfirm(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
                >
                  ×
                </button>
              </div>

              {/* Search */}
              <div className="px-4 pt-3 pb-2">
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={digestUserSearch}
                  onChange={(e) => setDigestUserSearch(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  autoFocus
                />
              </div>

              {/* User list */}
              <div className="flex-1 overflow-y-auto px-4 pb-4" style={{ minHeight: 0 }}>
                {usersLoading && (
                  <div className="text-center text-gray-400 py-6 text-sm">Loading users…</div>
                )}
                {!usersLoading && filtered.length === 0 && (
                  <div className="text-center text-gray-400 py-6 text-sm">No users found</div>
                )}
                {!usersLoading && filtered.map((u) => {
                  const name = displayName(u);
                  const ini = initials(u);
                  const email = u.email || '';
                  return (
                    <button
                      key={u._id}
                      disabled={digestSending}
                      onClick={() => confirmSendDigest(email)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-50 transition-colors text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                        style={{ background: '#2563eb' }}
                      >
                        {ini}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-800 truncate">{name || email}</div>
                        <div className="text-xs text-gray-400 truncate">{email}</div>
                      </div>
                      <span className="text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        {digestSending ? 'Sending…' : 'Send →'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
    </DashboardLayout>
  );
};

const initials = (u) => {
  if (!u) return '?';
  const f = u.profile?.firstName || u.firstName || '';
  const l = u.profile?.lastName || u.lastName || '';
  return ((f[0] || '') + (l[0] || '')).toUpperCase() || (u.email?.[0]?.toUpperCase() || '?');
};

const displayName = (u) => {
  if (!u) return '';
  const first = u.profile?.firstName || u.firstName || '';
  const last = u.profile?.lastName || u.lastName || '';
  return `${first} ${last}`.trim() || u.email || '';
};

const getMemberId = (member) => {
  if (!member) return '';
  if (typeof member === 'string' || typeof member === 'number') return String(member);
  return String(member._id || member.id || '');
};

const getProfilePicture = (member) => {
  if (!member || typeof member !== 'object') return '';
  return member.profile?.profilePicture || member.profilePicture || '';
};

const resolveMembers = (eventMembers = [], crew = null) => {
  const roster = [crew?.foreman, ...(crew?.members || [])].filter(Boolean);
  const rosterById = new Map();

  roster.forEach((member) => {
    const id = getMemberId(member);
    if (!id || rosterById.has(id)) return;
    rosterById.set(id, member);
  });

  const resolved = (eventMembers || [])
    .filter(Boolean)
    .map((member) => {
      if (typeof member === 'object') return member;
      const id = String(member);
      return rosterById.get(id) || { _id: id };
    });

  if (resolved.length > 0) return resolved;
  return Array.from(rosterById.values());
};
const avatarColor = (id = '') => {
  const palette = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#0EA5E9'];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

const CalendarEventTile = ({ evt, navigate, crews, onOpenEvent, hideTrades }) => {
  const e = evt.resource || {};
  const isAllDayFill = e.kind === 'allday_fill';
  const isJobWalk = e.eventType === 'job_walk';
  const isAssignment = e.eventType === 'crew_assignment';
  const isAllDayLane = Boolean(evt.allDay || e.allDay);
  const crew = crews.find((c) => String(c._id) === String(e.crew?._id || e.crew));
  const projectId = e.project?._id || e.project;
  const members = resolveMembers(e.members, crew);
  const memberNames = members.map((m) => displayName(m)).filter(Boolean);
  const memberTitle = e.memberNames || (memberNames.length > 0
    ? `${memberNames.slice(0, 2).join(', ')}${memberNames.length > 2 ? ` +${memberNames.length - 2}` : ''}`
    : '');
  const titleText = String(
    evt.title ||
    e.title ||
    memberTitle ||
    e.project?.title ||
    e.project?.name ||
    (isAssignment ? 'Crew assignment' : 'Untitled event')
  ).trim();
  const visibleMembers = members.slice(0, 3);
  const extraCount = members.length - visibleMembers.length;
  const avatarStack = visibleMembers.length > 0 && (
    <div className={`evt-avatars${isAllDayLane ? ' evt-avatars-compact' : ''}`}>
      {visibleMembers.map((m, idx) => {
        const id = getMemberId(m) || `member-${idx}`;
        const label = initials(m);
        const photo = getProfilePicture(m);
        const name = displayName(m) || 'Crew member';
        return (
          <span
            key={id}
            className="evt-avatar"
            style={{ background: avatarColor(id) }}
            title={name}
          >
            {photo ? (
              <img src={photo} alt={name} className="evt-avatar-img" />
            ) : (
              label
            )}
          </span>
        );
      })}
      {extraCount > 0 && (
        <span className="evt-avatar evt-avatar-more">+{extraCount}</span>
      )}
    </div>
  );
  const labels = (e.labels || []).filter(Boolean);
  const attachmentCount = e.attachments?.length || 0;
  const pmName = displayName(e.jobWalk?.projectManager);
  const leadName = displayName(e.jobWalk?.assignedLead);
  const walkNotes = (e.jobWalk?.notes || '').trim();
  const description = (e.description || '').trim();
  const labelText = labels.length > 0
    ? `${labels.slice(0, 2).join(', ')}${labels.length > 2 ? ` +${labels.length - 2}` : ''}`
    : '';
  const durationMs = Math.max(0, new Date(evt.end).getTime() - new Date(evt.start).getTime());
  const durationMinutes = Math.round(durationMs / (60 * 1000));

  const detailRows = [];
  if (isJobWalk && pmName) detailRows.push({ key: 'pm', label: 'PM', value: pmName });
  if (isJobWalk && leadName) detailRows.push({ key: 'lead', label: 'Lead', value: leadName });
  if (isJobWalk && walkNotes) detailRows.push({ key: 'notes', label: 'Notes', value: walkNotes });
  if (!isJobWalk && description) detailRows.push({ key: 'notes', label: 'Notes', value: description });
  if (labelText) detailRows.push({ key: 'tags', label: 'Tags', value: labelText });
  if (e.location) detailRows.push({ key: 'location', label: 'Loc', value: e.location });
  const maxDetails = e.allDay
    ? 3
    : durationMinutes >= 90
      ? 3
      : durationMinutes >= 45
        ? 2
        : durationMinutes >= 25
          ? 1
          : 0;
  const visibleDetails = detailRows.slice(0, maxDetails);

  const openProject = (ev) => {
    ev.stopPropagation();
    if (projectId) navigate('/projects', { state: { highlightProjectId: projectId } });
  };

  const openEdit = () => {
    const payload = evt?.resource || evt?.event || evt;
    if (!payload) return;
    onOpenEvent?.(payload);
  };

  if (isAllDayFill) {
    return (
      <div className="evt-card evt-card-fill" onClick={openEdit}>
        <div className="evt-body evt-body-fill">
          <span className="evt-title evt-title-fill">{titleText}</span>
        </div>
      </div>
    );
  }

  // Crew metadata to surface inline on the tile (trades, roster, project, location).
  const crewTrades = (crew?.trades || []).filter((t) => t && (t.name || t._id));
  const crewPeople = (crew?.people || []).filter((p) => p && (p.name || p._id));
  const peopleSummary = crewPeople.length > 0
    ? `${crewPeople.slice(0, 2).map((p) => p.name).join(', ')}${crewPeople.length > 2 ? ` +${crewPeople.length - 2}` : ''}`
    : '';
  const projectLabel = (() => {
    const proj = e.project;
    if (!proj || typeof proj !== 'object') return '';
    const job = proj.jobNumber || '';
    const client = proj.client?.name || '';
    if (job && client) return `${job} — ${client}`;
    return job || client || proj.title || proj.name || '';
  })();

  return (
    <div className="evt-card" onClick={openEdit}>
      <div className="evt-accent" />
      <div className="evt-body">
        <div className="evt-title-row">
          {!isAllDayLane && isJobWalk && <span className="evt-type-badge evt-type-walk">Walk</span>}
          {!isAllDayLane && isAssignment && <span className="evt-type-badge evt-type-job">Job</span>}
          <span className="evt-title">{titleText}</span>
          {isAllDayLane && isAssignment && avatarStack}
        </div>

        {isAllDayLane && ((!hideTrades && crewTrades.length > 0) || peopleSummary || projectLabel || e.location) && (
          <div className="evt-allday-meta">
            {!hideTrades && crewTrades.length > 0 && (
              <span className="evt-allday-chips">
                {crewTrades.slice(0, 4).map((t) => (
                  <span key={String(t._id || t)} className="evt-allday-chip">{t.name || 'Trade'}</span>
                ))}
                {crewTrades.length > 4 && (
                  <span className="evt-allday-chip">+{crewTrades.length - 4}</span>
                )}
              </span>
            )}
            {peopleSummary && (
              <span className="evt-allday-pill" title={crewPeople.map((p) => p.name).join(', ')}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>{peopleSummary}</span>
              </span>
            )}
            {projectLabel && (
              <span
                className="evt-allday-pill evt-allday-pill-link"
                title={`Open project: ${projectLabel}`}
                onClick={(ev) => { ev.stopPropagation(); if (projectId) openProject(ev); }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                <span>{projectLabel}</span>
              </span>
            )}
            {e.location && (
              <span className="evt-allday-pill" title={e.location}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span>{e.location}</span>
              </span>
            )}
          </div>
        )}

        {!isAllDayLane && visibleDetails.length > 0 && (
          <div className="evt-details">
            {visibleDetails.map((line) => (
              <span key={line.key} className={`evt-detail-badge evt-detail-${line.key}`} title={`${line.label}: ${line.value}`}>
                <span className="evt-detail-key">{line.label}</span>
                <span className="evt-detail-value">{line.value}</span>
              </span>
            ))}
          </div>
        )}

        {!isAllDayLane && <div className="evt-meta-row">
          <div className="evt-meta-left">
            {labels.length > 0 && (
              <span className="evt-meta-item" title="Labels">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><circle cx="7" cy="7" r="1.5" fill="currentColor"/></svg>
                {labels.length}
              </span>
            )}
            {attachmentCount > 0 && (
              <span className="evt-meta-item" title="Attachments">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21.44 11.05-9.19 9.19a6 6 0 1 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                {attachmentCount}
              </span>
            )}
            {crew && <span className="evt-meta-chip" style={{ ['--chip']: crew.color }}>{crew.name}</span>}
            {projectLabel && (
              <span
                className="evt-meta-chip evt-meta-chip-project"
                title={`Open project: ${projectLabel}`}
                onClick={(ev) => { ev.stopPropagation(); if (projectId) openProject(ev); }}
              >
                {projectLabel}
              </span>
            )}
            {e.location && <span className="evt-meta-chip evt-loc">{e.location}</span>}
          </div>

          <div className="evt-meta-right">
            {avatarStack}
            {projectId && (
              <button onClick={openProject} className="evt-open" title="Open project">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </button>
            )}
          </div>
        </div>}
      </div>
    </div>
  );
};

export default CalendarPage;
